/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
/**
 * {@inheritDoc @fluidframework/container-definitions#IContainerContext}
 */
export class ContainerContext {
    get clientId() {
        return this._getClientId();
    }
    /**
     * DISCLAIMER: this id is only for telemetry purposes. Not suitable for any other usages.
     */
    get id() {
        return this._getContainerDiagnosticId() ?? "";
    }
    /**
     * When true, ops are free to flow
     * When false, ops should be kept as pending or rejected
     */
    get connected() {
        return this._getConnected();
    }
    constructor(options, scope, baseSnapshot, _version, deltaManager, storage, quorum, audience, loader, submitFn, submitSummaryFn, 
    /**
     * @returns clientSequenceNumber of last message in a batch
     */
    submitBatchFn, 
    /**
     * `unknown` should be removed once `@alpha` tag is removed from IContainerContext
     * @see {@link https://dev.azure.com/fluidframework/internal/_workitems/edit/7462}
     * Any changes to submitSignalFn `content` should be checked internally by temporarily changing IContainerContext and removing all `unknown`s
     */
    submitSignalFn, disposeFn, closeFn, updateDirtyContainerState, getAbsoluteUrl, _getContainerDiagnosticId, _getClientId, _getAttachState, _getConnected, clientDetails, existing, taggedLogger, pendingLocalState, snapshotWithContents) {
        this.options = options;
        this.scope = scope;
        this.baseSnapshot = baseSnapshot;
        this._version = _version;
        this.deltaManager = deltaManager;
        this.storage = storage;
        this.quorum = quorum;
        this.audience = audience;
        this.loader = loader;
        this.submitFn = submitFn;
        this.submitSummaryFn = submitSummaryFn;
        this.submitBatchFn = submitBatchFn;
        this.submitSignalFn = submitSignalFn;
        this.disposeFn = disposeFn;
        this.closeFn = closeFn;
        this.updateDirtyContainerState = updateDirtyContainerState;
        this.getAbsoluteUrl = getAbsoluteUrl;
        this._getContainerDiagnosticId = _getContainerDiagnosticId;
        this._getClientId = _getClientId;
        this._getAttachState = _getAttachState;
        this._getConnected = _getConnected;
        this.clientDetails = clientDetails;
        this.existing = existing;
        this.taggedLogger = taggedLogger;
        this.pendingLocalState = pendingLocalState;
        this.snapshotWithContents = snapshotWithContents;
        this.supportedFeatures = new Map([
            /**
             * This version of the loader accepts `referenceSequenceNumber`, provided by the container runtime,
             * as a parameter to the `submitBatchFn` and `submitSummaryFn` functions.
             * This is then used to set the reference sequence numbers of the submitted ops in the DeltaManager.
             */
            ["referenceSequenceNumbers", true],
        ]);
    }
    getLoadedFromVersion() {
        return this._version;
    }
    get attachState() {
        return this._getAttachState();
    }
}
//# sourceMappingURL=containerContext.js.map